import React from 'react';
import clsx from 'clsx';

export interface StoreBadgeProps {
  platform: 'ios' | 'android';
  url?: string;
  className?: string;
  size?: 'md' | 'lg';
}

export const StoreBadge: React.FC<StoreBadgeProps> = ({
  platform,
  url,
  className,
  size = 'md',
}) => {
  // If no URL is provided, do not render an empty or broken button
  if (!url) return null;

  const sizeClasses = {
    md: 'h-11 px-4 text-xs',
    lg: 'h-12 px-5 text-sm',
  };

  const isIos = platform === 'ios';

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={isIos ? 'Download on the Apple App Store' : 'Get it on Google Play'}
      className={clsx(
        'inline-flex items-center gap-3 bg-black hover:bg-slate-900 active:bg-slate-950 text-white rounded-xl font-medium transition-all duration-150 shadow-xs hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 border border-slate-800 select-none min-h-[44px]',
        sizeClasses[size],
        className
      )}
    >
      {isIos ? (
        <>
          {/* Apple Logo SVG */}
          <svg className="w-5 h-5 fill-current shrink-0" viewBox="0 0 170 170" aria-hidden="true">
            <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.35.13-9.16-1.9-14.42-6.08-3.69-3.04-7.67-7.87-11.94-14.49-6.3-9.76-11.3-21.2-15-34.33-3.7-13.13-5.55-25.29-5.55-36.48 0-14.12 3.65-25.9 10.96-35.34 7.31-9.44 16.32-14.28 27.03-14.53 4.9 0 10.51 1.41 16.84 4.24 6.33 2.82 10.22 4.29 11.66 4.39 1.63-.1 5.76-1.63 12.38-4.58 6.63-2.95 12.23-4.3 17.81-4.04 13.06.63 23.49 5.38 31.29 14.25-11.43 6.94-17.02 16.48-16.78 28.62.24 9.68 3.99 17.65 11.24 23.91 7.25 6.27 15.93 9.77 26.04 10.51-2.07 6.43-4.57 13.02-7.5 19.77zM119.22 31.86c0-7.39 2.66-14.23 7.97-20.52 5.31-6.29 11.83-10.37 19.57-12.24.43 2.07.65 4.09.65 6.06 0 7.39-2.83 14.45-8.5 21.18-5.66 6.74-12.25 10.74-19.78 12.01-.22-2.19-.34-4.28-.34-6.49z"/>
          </svg>
          <div className="flex flex-col text-left leading-tight">
            <span className="text-[10px] text-slate-300 font-normal">Download on the</span>
            <span className="text-xs sm:text-sm font-semibold tracking-tight">App Store</span>
          </div>
        </>
      ) : (
        <>
          {/* Google Play Logo SVG */}
          <svg className="w-5 h-5 shrink-0" viewBox="0 0 512 512" aria-hidden="true">
            <path fill="#4285F4" d="M47.2 24.3C38.4 33.7 33 47.9 33 65.5v381c0 17.6 5.4 31.8 14.2 41.2l2.3 2.3L268 271.5v-31L49.5 22l-2.3 2.3z"/>
            <path fill="#EA4335" d="M344.2 347.7l-76.2-76.2v-31l76.2-76.2 1.7 1 90.3 51.3c25.8 14.6 25.8 38.6 0 53.3l-90.3 51.3-1.7 1.1z"/>
            <path fill="#FBBC04" d="M268 271.5L47.2 492.3c8.9 9.4 23.5 10.6 40 1.2l257-145.8-76.2-76.2z"/>
            <path fill="#34A853" d="M268 240.5L87.2 19.7C70.7 10.3 56.1 11.5 47.2 20.9L268 240.5z"/>
          </svg>
          <div className="flex flex-col text-left leading-tight">
            <span className="text-[10px] text-slate-300 font-normal uppercase tracking-wider">GET IT ON</span>
            <span className="text-xs sm:text-sm font-semibold tracking-tight">Google Play</span>
          </div>
        </>
      )}
    </a>
  );
};
